import React, { createContext, useContext, useState, useEffect, useCallback } from "react";
import { authApi } from "../services/api.service";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user,    setUser]    = useState(() => {
    try { return JSON.parse(localStorage.getItem("at_user")) || null; }
    catch { return null; }
  });
  const [token,   setToken]   = useState(() => localStorage.getItem("at_token") || null);
  const [loading, setLoading] = useState(false);
  const [error,   setError]   = useState(null);

  // Listen for 401 global logout events
  useEffect(() => {
    const handler = () => { setUser(null); setToken(null); };
    window.addEventListener("at_logout", handler);
    return () => window.removeEventListener("at_logout", handler);
  }, []);

  // ── Login ──────────────────────────────────────────────────
  const login = useCallback(async ({ mobile, password }) => {
    setLoading(true);
    setError(null);
    try {
      // Get rough geolocation for the API (optional — falls back to Mumbai)
      let latitude = "19.0760", longitude = "72.8777", address = "India";
      try {
        await new Promise(resolve => {
          navigator.geolocation.getCurrentPosition(
            pos => { latitude = String(pos.coords.latitude); longitude = String(pos.coords.longitude); resolve(); },
            () => resolve(),
            { timeout: 3000 }
          );
        });
      } catch { /* use defaults */ }

      const res = await authApi.login({
        mobile,
        password,
        fcmToken: "web_fcm_placeholder",
        latitude,
        longitude,
        address,
      });

      const { accessToken, user: userData, status, message } = res.data;

      if (!status) throw new Error(message || "Login failed");

      // Only allow SUPER_ADMIN, ADMIN, STAFF roles
      if (!["SUPER_ADMIN", "ADMIN", "STAFF"].includes(userData.role)) {
        throw new Error("Access denied. Super Admin, Admin, or Staff role required.");
      }

      localStorage.setItem("at_token", accessToken);
      localStorage.setItem("at_user", JSON.stringify(userData));
      setToken(accessToken);
      setUser(userData);
      return userData;
    } catch (err) {
      const msg = err.response?.data?.message || err.message || "Login failed";
      setError(msg);
      throw new Error(msg);
    } finally {
      setLoading(false);
    }
  }, []);

  // ── Logout ─────────────────────────────────────────────────
  const logout = useCallback(async () => {
    await authApi.logout();
    localStorage.removeItem("at_token");
    localStorage.removeItem("at_user");
    setUser(null);
    setToken(null);
  }, []);

  const isAuthenticated = !!token && !!user;

  return (
    <AuthContext.Provider value={{ user, token, loading, error, login, logout, isAuthenticated }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
